import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

// hooks
import useAuth from "hooks/useAuth";
import useDiscountsTable from "./hooks/useDiscountsTable";

// reusable components
import { SecureTableShell } from "@/components/ui/SecureTableShell";
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import DiscountSearchControls from "./components/DiscountSearchControls";

// helpers
import { getFallbackStateDiscounts } from "./utils/GetFallbackStateDiscounts";
import { useStorePath } from "@hooks/useStorePath";

const Discounts = () => {
  // Destruct exports from the main hook
  const {
    allDiscounts,
    discounts: filteredDiscounts,
    searchTerm,
    updateSearchTerm,
    discountColumns,
    renderDiscountRow,
    isLoading,
    isError,
    refetch,
  } = useDiscountsTable();

  // GEt fallback status from the helper
  const fallback = getFallbackStateDiscounts({
    isLoading,
    allData: allDiscounts,
    filteredData: filteredDiscounts,
  });

  const navigate = useNavigate();
  const { storePath } = useStorePath();

  // Search UI
  const [rawSearchTerm, setRawSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const timeout = setTimeout(() => {
      updateSearchTerm(rawSearchTerm);
    }, 500);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm, updateSearchTerm]);

  // Making a new OR viweing a discount
  const handleCreateDiscount = () => {
    navigate(storePath("discounts/new"));
  };

  // Check if user is signed in
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // Error fallback
  if (isError) {
    return (
      <div className="p-6">
        <EmptyStateCard
          icon={
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full">
              <span className="text-red-600 font-bold text-lg">!</span>
            </div>
          }
          title="Something went wrong"
          description="We couldn't load your discounts. Please try again or contact support."
          buttonLabel="Retry"
          onAction={refetch}
        />
      </div>
    );
  }

  return (
    <div className="pt-[2em]">
      <div className="flex flex-row justify-end items-center mb-[1em]">
        {/* Create discount button */}
        {/* TODO: Handle creation of a discount properly */}
        <button
          className="bg-[#000] text-white text-[14px] px-3 py-2 rounded-lg font-bold hover:bg-[#424242]"
          onClick={handleCreateDiscount}
        >
          Create New Discount
        </button>
      </div>

      {/* Search control */}
      {/* Implement the filter controls */}
      <DiscountSearchControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
      />

      {/* Discounts Table */}
      <SecureTableShell
        data={filteredDiscounts}
        columns={discountColumns}
        renderRow={renderDiscountRow}
        fallbackState={fallback.state}
      />
    </div>
  );
};

export default Discounts;